import type { BlogNewsListItemProp } from '@/services';
import { toEpoch } from './date';
import { resolvePostListImage } from './resolve-post-image';

/**
 * 카드·목록 영역에서 공용으로 쓰는 포스트 요약.
 * 시리즈·태그·관련 글 캐시가 모두 이 형태를 저장하며, 추가 필드는 확장해서 붙인다(시리즈는 `lang`).
 */
export interface PostSummary {
  /** WP GraphQL id — 카드 gtag label(`blog_item-{id}`)에 쓴다 */
  id: string;
  slug: string;
  title: string;
  category: string;
  image: string;
  createdAt: string; // ISO 8601 (ACF blog.date)
}

/** 실제로 쓰는 필드만 좁혀 받아 목록 아이템의 무관한 변경에 끌려다니지 않게 한다 */
export type PostSummarySource = Pick<
  BlogNewsListItemProp,
  | 'id'
  | 'slug'
  | 'title'
  | 'lang'
  | 'categoryKo'
  | 'categoryEn'
  | 'date'
  | 'heroImage'
  | 'featuredImage'
>;

/**
 * 목록 카드 섹션(Section.Main — news)이 실제로 읽는 필드 계약.
 * 리치 목록 아이템과 뉴스 저자 페이지 어댑터(toAuthorPageListItem) 둘 다 이 형태로
 * 들어온다 — 카드가 새 필드를 읽으려면 여기 추가해야 하므로, 공급자 쪽 누락이
 * 컴파일 에러로 드러난다.
 */
export type PostCardListItem = Pick<
  BlogNewsListItemProp,
  | 'id'
  | 'slug'
  | 'title'
  | 'categoryKo'
  | 'categoryEn'
  | 'date'
  | 'heroImage'
  | 'featuredImage'
>;

/**
 * 카테고리는 포스트 자신의 언어 기준으로 고른다 — ko 포스트는 `categoryEn` 이 비어 있다.
 * 이미지는 목록 아이템에 content·seoOpengraphImage 가 없어 heroImage → featuredImage 까지만 적용된다.
 */
export const toPostSummary = (item: PostSummarySource): PostSummary => {
  const category = item.lang?.includes('ko')
    ? item.categoryKo?.[0]
    : item.categoryEn?.[0];

  return {
    id: item.id,
    slug: item.slug,
    title: item.title,
    category: category ?? '',
    image: resolvePostListImage(item) ?? '',
    createdAt: item.date,
  };
};

/** 로케일별로 분리된 포스트 요약 — 태그·저자 캐시가 이 형태로 박제한다 */
export interface PostSummariesByLocale {
  ko: PostSummary[];
  en: PostSummary[];
}

/**
 * 목록 아이템들을 로케일(ko/en)별 요약 목록으로 분리하고 date 내림차순(최신순)으로
 * 정렬한다 — 태그·저자 캐시 빌드가 공유하는 규칙(포스트는 단일 언어라 자기 lang
 * 배열에만 들어간다).
 */
export const splitPostSummariesByLocale = (
  items: PostSummarySource[],
): PostSummariesByLocale => {
  const { ko, en } = items.reduce<PostSummariesByLocale>(
    (acc, item) => {
      const summary = toPostSummary(item);
      if (item.lang?.includes('ko')) acc.ko.push(summary);
      if (item.lang?.includes('en')) acc.en.push(summary);
      return acc;
    },
    { ko: [], en: [] },
  );

  const latestFirst = (a: PostSummary, b: PostSummary) =>
    toEpoch(b.createdAt) - toEpoch(a.createdAt);

  ko.sort(latestFirst);
  en.sort(latestFirst);

  return { ko, en };
};
